import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Lock,
  CreditCard,
  Users,
  Calendar,
  Loader2,
  AlertCircle,
  Info,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getCsrfHeaders } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus, type SubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useToast } from "@/hooks/use-toast";
import type { User as UserType, AccountMember } from "@shared/schema";
import PartnerInviteLinkCard from "@/components/PartnerInviteLinkCard";
import { BILLING_OWNER_ONLY_MESSAGE, getBillingPlanName } from "@shared/billingPermissions";
import { formatBillingEndDate, getCancellationMessage } from "@shared/subscriptionBilling";
import { BILLING_PORTAL_ERROR, requestSettingsBillingPortal, refreshSettingsBilling, watchSettingsBillingReturn } from "@/lib/settingsBilling";

const CANCEL_REASONS = [
  "Too expensive",
  "Not enough activity",
  "Missing features I need",
  "Just testing",
  "Other",
];

export default function Settings() {
  const { user, isTeamMember } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const { data: currentUser, isLoading: userLoading } = useQuery<UserType>({
    queryKey: ["/api/auth/user"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  useEffect(() => watchSettingsBillingReturn(() => {
    void refreshSettingsBilling(queryClient, false);
  }), [queryClient]);

  const [personalForm, setPersonalForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [personalFormInit, setPersonalFormInit] = useState(false);

  useEffect(() => {
    if (currentUser && !personalFormInit) {
      setPersonalForm({
        firstName: currentUser.firstName ?? "",
        lastName: currentUser.lastName ?? "",
        phone: currentUser.phone ?? "",
      });
      setPersonalFormInit(true);
    }
  }, [currentUser?.id]);

  const updatePersonalMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", "/api/profile", {
        firstName: personalForm.firstName,
        lastName: personalForm.lastName,
        phone: personalForm.phone,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: "Personal information updated", description: "Your changes have been saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update profile.", variant: "destructive" });
    },
  });

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");

  const changePasswordMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      }),
    onSuccess: () => {
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordError("");
      toast({ title: "Password updated", description: "Your password has been changed successfully." });
    },
    onError: (error: Error) => {
      const msg = error.message || "";
      if (msg.includes("incorrect") || msg.includes("401")) {
        setPasswordError("Your current password is incorrect. Please try again.");
      } else if (msg.includes("400")) {
        setPasswordError("Please check your entries and try again.");
      } else {
        setPasswordError("Something went wrong. Please try again.");
      }
    },
  });

  const handleChangePassword = () => {
    setPasswordError("");
    if (!passwordData.currentPassword) { setPasswordError("Please enter your current password."); return; }
    if (passwordData.newPassword.length < 8) { setPasswordError("New password must be at least 8 characters."); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { setPasswordError("New passwords do not match."); return; }
    changePasswordMutation.mutate();
  };

  const membershipTier = user?.membershipTier || "Free";
  const isPaid = membershipTier === "Paid" || membershipTier === "Annual" || membershipTier === "SearchAndBid";
  const isCancelledOrSuspended = membershipTier === "Cancelled" || membershipTier === "Suspended";
  const requiresBillingManagement = !!user?.stripeSubscriptionId && membershipTier !== "Cancelled";
  const billingCycle = user?.billingCycle || "monthly";
  const isAnnual = billingCycle === "annual";
  const userRole = (user?.userType as string) || "Shipper";

  const { data: subStatus } = useSubscriptionStatus(false, { refreshOnReturn: true });

  const periodEndDate = formatBillingEndDate(subStatus?.currentPeriodEnd);
  const cancelAtPeriodEnd = subStatus?.cancelAtPeriodEnd ?? false;

  const [openingPortal, setOpeningPortal] = useState(false);
  const handleManageBilling = async () => {
    if (isTeamMember) return;
    setOpeningPortal(true);
    try {
      const url = await requestSettingsBillingPortal(getCsrfHeaders("POST"));
      window.location.href = url;
    } catch (error: any) {
      toast({ title: "Error", description: error.impersonationReadOnly ? error.message : BILLING_PORTAL_ERROR, variant: "destructive" });
    } finally {
      setOpeningPortal(false);
    }
  };

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelFeedback, setCancelFeedback] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleConfirmCancel = async () => {
    if (isTeamMember) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await fetch("/api/stripe/cancel-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({ reason: cancelReason || undefined, feedback: cancelFeedback || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to cancel subscription");
      setShowCancelDialog(false);
      setCancelReason("");
      setCancelFeedback("");
      queryClient.setQueryData<SubscriptionStatus>(["/api/stripe/subscription-status"], previous => ({
        currentPeriodEnd: typeof data.effectiveDate === "number" ? data.effectiveDate : null,
        cancelAtPeriodEnd: true,
        paymentProblem: previous?.paymentProblem ?? false,
      }));
      await refreshSettingsBilling(queryClient);
      toast({
        title: "Subscription cancelled",
        description: getCancellationMessage(data.effectiveDate),
      });
    } catch (error: any) {
      setCancelError(error.message || "Something went wrong. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  // ── Team Access ──────────────────────────────────────────────────────────────
  const isSecondaryMember = !!user?.accountOwnerId;

  const {
    data: membersData,
    isLoading: membersLoading,
    isError: membersError,
    refetch: refetchMembers,
  } = useQuery<{ members: AccountMember[] }>({
    queryKey: ["/api/account/members"],
    enabled: !isSecondaryMember,
  });

  const acceptedMembers = (membersData?.members ?? []).filter((m) => m.inviteAcceptedAt !== null);
  const pendingMembers = (membersData?.members ?? []).filter((m) => m.inviteAcceptedAt === null);
  const allMembers = membersData?.members ?? [];
  const seatsUsed = 1 + acceptedMembers.length;
  const canInvite = allMembers.length < 1; // owner + 0 members = 1 seat, max is 2

  const [inviteForm, setInviteForm] = useState({ firstName: "", lastName: "", email: "" });
  const [inviteError, setInviteError] = useState("");
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<AccountMember | null>(null);

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/account/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        credentials: "include",
        body: JSON.stringify(inviteForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send invitation");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Invitation sent", description: `Invitation sent to ${inviteForm.email}` });
      setInviteForm({ firstName: "", lastName: "", email: "" });
      setInviteError("");
      refetchMembers();
    },
    onError: (error: Error) => {
      setInviteError(error.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await fetch(`/api/account/members/${encodeURIComponent(memberId)}`, {
        method: "DELETE",
        headers: { ...getCsrfHeaders("DELETE") },
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to remove team member");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Team member removed" });
      setConfirmRemoveMember(null);
      refetchMembers();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setConfirmRemoveMember(null);
    },
  });

  function getInitialsFromEmail(email: string): string {
    return (email?.[0] ?? "?").toUpperCase();
  }

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault();
    setInviteError("");
    inviteMutation.mutate();
  }

  // ── End Team Access ───────────────────────────────────────────────────────────

  const cancelDialogBodyText = periodEndDate
    ? `Your subscription will remain active until ${periodEndDate}. After that date, you will lose access to Fill The Trucks and will not be charged again.`
    : "Your subscription will remain active until the end of your current billing period. After that, you will lose access to Fill The Trucks and will not be charged again.";

  if (userLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Settings</h1>
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold">Settings</h1>

      {/* Section 1 — Personal Information */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-5">
          <User className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Personal Information</h2>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="settings-first-name">First Name</Label>
              <Input
                id="settings-first-name"
                value={personalForm.firstName}
                onChange={(e) => setPersonalForm({ ...personalForm, firstName: e.target.value })}
                data-testid="input-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-last-name">Last Name</Label>
              <Input
                id="settings-last-name"
                value={personalForm.lastName}
                onChange={(e) => setPersonalForm({ ...personalForm, lastName: e.target.value })}
                data-testid="input-last-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="settings-email">Email</Label>
              <Input
                id="settings-email"
                type="email"
                value={currentUser?.email || ""}
                disabled
                className="bg-muted"
                data-testid="input-email"
              />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-phone">Phone Number</Label>
              <Input
                id="settings-phone"
                type="tel"
                value={personalForm.phone}
                onChange={(e) => setPersonalForm({ ...personalForm, phone: e.target.value })}
                data-testid="input-phone"
              />
            </div>
          </div>
          <Button
            onClick={() => updatePersonalMutation.mutate()}
            disabled={updatePersonalMutation.isPending}
            data-testid="button-save-personal"
          >
            {updatePersonalMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </Card>

      {/* Section 2 — Security */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Security</h2>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-medium">Password</p>
            <p className="text-sm text-muted-foreground">Update your account password.</p>
          </div>
          <Button
            variant="outline"
            onClick={() => { setPasswordError(""); setPasswordDialogOpen(true); }}
            data-testid="button-change-password"
          >
            Change Password
          </Button>
        </div>
      </Card>

      {/* Section 3 — Membership & Billing */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-5">
          <CreditCard className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Membership &amp; Billing</h2>
        </div>

        {/* Current Plan — shown for paid users only */}
        {isPaid && (
          <div className="mb-5" data-testid="card-current-plan">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium mb-1">Current Plan</p>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{getBillingPlanName(user)}</span>
                  <>
                    <span>•</span>
                    <span>{isAnnual ? "Annual" : "Monthly"} billing</span>
                  </>
                </div>

                {cancelAtPeriodEnd && (
                  <p className="text-xs text-amber-600 mt-1">
                    {getCancellationMessage(subStatus?.currentPeriodEnd)}
                  </p>
                )}
              </div>

              <Badge data-testid="badge-billing-status" className={`no-default-hover-elevate no-default-active-elevate flex-shrink-0 ${
                subStatus?.paymentProblem || cancelAtPeriodEnd
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                  : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              }`}>
                {subStatus?.paymentProblem ? "Payment problem" : cancelAtPeriodEnd ? "Cancelling" : "Active"}
              </Badge>
            </div>
          </div>
        )}

        {isPaid && <Separator className="my-5" />}

        {/* Billing actions */}
        {isTeamMember ? (
          <div data-testid="card-billing-account">
            {!isPaid && <p className="mb-2">{getBillingPlanName(user)} — {membershipTier === "Free" ? "Active" : membershipTier}</p>}
            <p className="text-sm text-muted-foreground">{BILLING_OWNER_ONLY_MESSAGE}</p>
          </div>
        ) : isCancelledOrSuspended ? (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200" data-testid="card-billing-account">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                {membershipTier === "Cancelled"
                  ? "Your subscription has ended."
                  : requiresBillingManagement
                    ? "There is a problem with your payment."
                    : "Your subscription ended because a payment could not be collected."}
              </p>
              <div className="mt-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                <Button
                  className="w-full sm:w-auto"
                  onClick={requiresBillingManagement ? handleManageBilling : () => setLocation("/upgrade")}
                  disabled={requiresBillingManagement && openingPortal}
                  data-testid={requiresBillingManagement ? "button-update-payment" : "button-choose-plan"}
                >
                  {requiresBillingManagement && openingPortal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {requiresBillingManagement ? "Update payment" : "Choose a plan"}
                </Button>
                <a href="/support" className="text-xs underline underline-offset-2" data-testid="link-contact-support">
                  Contact support
                </a>
              </div>
            </div>
          </div>
        ) : membershipTier === "Free" ? (
          <div className="flex flex-col gap-4" data-testid="card-billing-account">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm text-muted-foreground mb-0.5">Current plan</p>
                <p className="font-medium">Free</p>
                <p className="text-sm text-muted-foreground">
                  {userRole === "Shipper" ? "3 shipments per month" :
                   userRole === "Carrier" ? "Bid on invited loads only" :
                   "Bid on invited loads only"}
                </p>
              </div>
              <Badge className="no-default-hover-elevate no-default-active-elevate bg-muted text-muted-foreground">
                Active
              </Badge>
            </div>

            <div className="border border-primary rounded-lg p-4 bg-primary/5">
              <p className="font-medium text-sm mb-1">
                {userRole === "Shipper" ? "Upgrade to Unlimited" :
                 userRole === "Carrier" ? "Upgrade to Search & Bid" :
                 "Upgrade your plan"}
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                {userRole === "Shipper"
                  ? "Post unlimited shipments, notify all your partners automatically, and compare bids side by side."
                  : userRole === "Carrier"
                  ? "Search the full public load board and bid on any available load."
                  : "Search and bid on the full public load board, or upgrade further to post loads for your shipper clients."}
              </p>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xl font-semibold">
                  {userRole === "Broker" ? "$25" : "$25"}
                  <span className="text-sm font-normal text-muted-foreground">/month</span>
                </span>
                <span className="text-sm text-muted-foreground">
                  or {userRole === "Broker" ? "$255" : "$255"}/year — save 15%
                </span>
              </div>
              <Button
                className="w-full"
                onClick={() => setLocation("/upgrade")}
                data-testid="button-upgrade-plan"
              >
                Upgrade your plan
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="card-billing-account">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-medium">Manage Billing</p>
                <p className="text-sm text-muted-foreground">Update payment method, view invoices, and manage billing details.</p>
              </div>
              <Button variant="outline" onClick={handleManageBilling} disabled={openingPortal} data-testid="button-manage-billing">
                {openingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Manage Billing
              </Button>
            </div>

            {isPaid && !cancelAtPeriodEnd && (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-medium">Change Plan</p>
                  <p className="text-sm text-muted-foreground">Switch billing cycle or plan. Proration is handled automatically.</p>
                </div>
                <Button variant="outline" onClick={handleManageBilling} disabled={openingPortal} data-testid="button-change-plan">
                  {openingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Change Plan
                </Button>
              </div>
            )}

            {isPaid && !cancelAtPeriodEnd && (
              <div className="pt-2 border-t">
                <button
                  onClick={() => { setCancelError(null); setCancelReason(""); setShowCancelDialog(true); }}
                  className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  data-testid="button-cancel-subscription"
                >
                  Cancel subscription
                </button>
              </div>
            )}
            {cancelAtPeriodEnd && periodEndDate && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">
                  Your subscription is set to cancel on {periodEndDate}. To reactivate, use Manage Billing above.
                </p>
              </div>
            )}
          </div>
        )}

      </Card>

      {/* Section 4 — Partner Invite Link (Shippers + eligible Brokers only) */}
      <PartnerInviteLinkCard />

      {/* Section 5 — Team Access */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Team Access</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Share account access with a team member. Each account includes 2 seats.
        </p>

        {isSecondaryMember ? (
          // ── Secondary member read-only view ──────────────────────────────
          <div className="space-y-4">
            <div className="rounded-md border divide-y">
              {/* Owner row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                  {((user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? "")).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight truncate">
                    {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Account Owner"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email ?? ""}</p>
                </div>
                <Badge variant="secondary" className="no-default-hover-elevate no-default-active-elevate flex-shrink-0">Owner</Badge>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Contact the account owner to manage team access.</p>
          </div>
        ) : (
          // ── Account owner full view ───────────────────────────────────────
          <div className="space-y-5">
            {/* Seat usage */}
            <p className="text-sm text-muted-foreground" data-testid="text-seat-usage">
              <span className="font-medium text-foreground">{membersLoading ? "—" : seatsUsed} of 2</span> seats used
            </p>

            {/* Members list */}
            {membersLoading ? (
              <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading team members...
              </div>
            ) : membersError ? (
              <div className="flex items-center gap-3 py-4">
                <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                <p className="text-sm text-muted-foreground">Failed to load team members.</p>
                <Button size="sm" variant="ghost" onClick={() => refetchMembers()} data-testid="button-retry-members">
                  <RefreshCw className="w-3 h-3 mr-1" />Retry
                </Button>
              </div>
            ) : (
              <div className="rounded-md border divide-y" data-testid="list-team-members">
                {/* Owner row — always first */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                    {((user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? "")).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight truncate">
                      {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "You"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email ?? ""}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="secondary" className="no-default-hover-elevate no-default-active-elevate">Owner</Badge>
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate">Active</Badge>
                  </div>
                </div>

                {/* Member rows */}
                {allMembers.map((member) => (
                  <div key={member.id} className="flex items-center gap-3 px-4 py-3" data-testid={`row-member-${member.id}`}>
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground flex-shrink-0">
                      {getInitialsFromEmail(member.invitedEmail ?? "")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight truncate">{member.invitedEmail}</p>
                      <p className="text-xs text-muted-foreground">Member</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="secondary" className="no-default-hover-elevate no-default-active-elevate">Member</Badge>
                      {member.inviteAcceptedAt ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 no-default-hover-elevate no-default-active-elevate">Active</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 no-default-hover-elevate no-default-active-elevate">Invite Pending</Badge>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmRemoveMember(member)}
                        data-testid={`button-remove-member-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Separator />

            {/* Invite form or seats-full message */}
            {canInvite ? (
              <form onSubmit={handleInviteSubmit} className="space-y-4" data-testid="form-invite-member">
                <p className="text-sm font-medium">Invite a Team Member</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="invite-first-name">First Name</Label>
                    <Input
                      id="invite-first-name"
                      value={inviteForm.firstName}
                      onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                      
                      required
                      data-testid="input-invite-first-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="invite-last-name">Last Name</Label>
                    <Input
                      id="invite-last-name"
                      value={inviteForm.lastName}
                      onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                      
                      required
                      data-testid="input-invite-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="invite-email">Business Email Address</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    
                    required
                    data-testid="input-invite-email"
                  />
                </div>
                {inviteError && (
                  <p className="text-sm text-destructive" data-testid="text-invite-error">{inviteError}</p>
                )}
                <Button type="submit" disabled={inviteMutation.isPending} data-testid="button-send-invitation">
                  {inviteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Send Invitation
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="text-seats-full">
                You have used all available seats. Remove a team member to invite someone new.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={!!confirmRemoveMember} onOpenChange={(open) => { if (!open) setConfirmRemoveMember(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{confirmRemoveMember?.invitedEmail}</strong> from your account?
              {confirmRemoveMember && !confirmRemoveMember.inviteAcceptedAt && " This will cancel the pending invitation."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemoveMember(null)} data-testid="button-cancel-remove">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={removeMutation.isPending}
              onClick={() => confirmRemoveMember && removeMutation.mutate(confirmRemoveMember.memberUserId)}
              data-testid="button-confirm-remove"
            >
              {removeMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) { setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" }); setPasswordError(""); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                data-testid="input-confirm-password"
              />
            </div>
            {passwordError && (
              <p className="text-sm text-destructive" data-testid="text-password-error">{passwordError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} data-testid="button-cancel-password">
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changePasswordMutation.isPending} data-testid="button-update-password">
              {changePasswordMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog && !isTeamMember} onOpenChange={setShowCancelDialog}>
        <DialogContent data-testid="dialog-cancel-subscription">
          <DialogHeader>
            <DialogTitle>Cancel your subscription?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{cancelDialogBodyText}</p>
          <p className="text-xs text-muted-foreground">We're sorry to see you go. We'd love to hear what we could do better.</p>
          <div className="mt-2">
            <p className="text-sm font-medium mb-2">Why are you cancelling? (optional)</p>
            <Select value={cancelReason} onValueChange={setCancelReason}>
              <SelectTrigger data-testid="select-cancel-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            data-testid="textarea-cancel-feedback"
            placeholder="Anything else you'd like to share? (optional)"
            rows={3}
            value={cancelFeedback}
            onChange={(e) => setCancelFeedback(e.target.value)}
          />
          {cancelError && (
            <p className="text-sm text-destructive mt-2" data-testid="text-cancel-error">{cancelError}</p>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="default" onClick={() => setShowCancelDialog(false)} disabled={cancelling} data-testid="button-keep-subscription">
              Keep my subscription
            </Button>
            <Button
              variant="outline"
              className="border-red-500 text-red-600 dark:text-red-400 dark:border-red-500"
              onClick={handleConfirmCancel}
              disabled={cancelling}
              data-testid="button-confirm-cancel"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Yes, cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
